import React from 'react'
import {includes, debounce} from 'lodash'
import {
  Card,
  CardActions,
  CardText,
  CardTitle
} from 'material-ui-legacy/Card'
import SelectField from 'material-ui-legacy/SelectField'
import MenuItem from 'material-ui-legacy/MenuItem'
import Row from 'muicss/lib/react/row'
import Col from 'muicss/lib/react/col'

import auth from '../../stores/auth.js'
import ValidatedTextInput from '../UI/ValidatedTextInput'
import ValidatedPasswordInput from '../UI/ValidatedPasswordInput'
import SaveButton from '../UI/SaveButton'
import CancelButton from '../UI/CancelButton'

/*
Props:
- user: a complete user object
  or
- User fields: will be inserted in the form when present
- submitCallback: callback function called on form submission. This is *only*
  called when the form is valid and the contents have changed. Receives user
  attributes.
- cancelCallback: callback function for the cancel button.
- changedCallback: callback function called when the form contents have changed.
  Receives true if the contents have changed.
- forceChanged: force the form status to "changed"
- myAccount: Boolean(false) - change form behaviours if user is in /my-account
  Right now this means omitting the `roles` field.
*/
class UserForm extends React.PureComponent {
  constructor(props) {
    super(props)
    // We have to build the state on a regular variable so
    // React doesn't complain about direct state mutation.
    let initialState = {
      formValid: false,
      formChanged: (this.props.forceChanged || false),
      formSubmitted: false,
      passwordMatchError: ''
    }

    // We can either receive a single data prop or each field separately
    this.initialData =
      (this.props.user) ? this.props.user.attributes : this.props

    this.fields = [
      'firstName',
      'lastName',
      'title',
      'email',
      'address',
      'city',
      'province',
      'postalCode',
      'country',
      'mobilePhone',
      'officePhone',
      'twitter',
      'facebook',
      'roles',
      'password',
      'passwordConfirmation'
    ]
    for (let field of this.fields) {
      initialState[field] = {
        value: (this.initialData[field] || ''),
        valid: true
      }
    }

    // Phones need tweaking
    if (this.initialData.phones) {
      initialState.mobilePhone = {
        value: this.initialData.phones.mobile,
        valid: true
      }
      initialState.officePhone = {
        value: this.initialData.phones.office,
        valid: true
      }
    }

    // Et voilà!
    this.state = initialState

    // Also defining possible roles here
    // TODO: Maybe make this come from the server in the future?
    this.possibleRoles = ['producer', 'admin']

    // Debouncing change and validation hooks for better performance
    this._checkForChanges = debounce(this._checkForChanges, 200)
    this._validateForm = debounce(this._validateForm, 200)
  }

  componentDidMount() {
    if (this.props.forceChanged) this._validateForm()
  }

  _submit = () => {
    // Don't do anything if we have invalid fields or no changes.
    if (!this.state.formValid || !this.state.formChanged) return

    let attributes = {}
    for (let field of this.fields) {
      attributes[field] = this.state[field].value
    }
    this.props.submitCallback(attributes)
    this.setState({formChanged: false, formSubmitted: true})
  }

  _change = (value, valid, attribute) => {
    let updates = {}
    updates[attribute] = {value: value, valid: valid}
    this.setState(
      updates,
      () => {
        this._checkForChanges()
        this._validateForm()
      }
    )
  }

  _checkForChanges = () => {
    let changed = (this.props.forceChanged || false)
    for (let field of this.fields) {
      let current = this.state[field].value
      let original = this.initialData[field] || ''
      if (current !== original) {
        changed = true
        break
      }
    }
    this.setState(
      {formChanged: changed},
      () => {
        if (this.props.changedCallback) {
          this.props.changedCallback(this.state.formChanged)
        }
      }
    )
  }

  _validateForm = () => {
    let allValid = true
    let passwordMatchError = ''
    for (let field of this.fields) {
      if (!this.state[field].valid) {
        allValid = false
        break
      }
    }
    if (this.state.password.value !== this.state.passwordConfirmation.value) {
      allValid = false
      passwordMatchError = 'The passwords must match'
    }
    this.setState({formValid: allValid, passwordMatchError: passwordMatchError})
  }

  render() {
    return (
      <Card>

        <CardTitle title={this.props.formTitle || "Basic info"} />

        <CardText>
          <Row>
            <Col lg="4" md="12">
              <ValidatedTextInput
                name="firstName"
                required={true}
                errorMessage="First name is required"
                hintText="First name"
                floatingLabelText="Fist name (required)"
                fullWidth={true}
                value={this.state.firstName.value}
                validatedOnChange={this._change}
              />
            </Col>

            <Col lg="4" md="12">
              <ValidatedTextInput
                name="lastName"
                hintText="Last name"
                floatingLabelText="Last name"
                fullWidth={true}
                value={this.state.lastName.value}
                validatedOnChange={this._change}
              />
            </Col>

            <Col lg="4" md="12">
              <ValidatedTextInput
                name="title"
                hintText="Creative producer"
                floatingLabelText="Title"
                fullWidth={true}
                value={this.state.title.value}
                validatedOnChange={this._change}
              />
            </Col>
          </Row>
        </CardText>


        <CardTitle
          title={
            (auth.isAdmin() && !this.props.myAccount) ?
            "Login and roles" :
            "Login"
          }
          subtitle="Leave password blank if you don't want to change it"
        />

        <CardText>
          <Row>
            <Col lg="4" md="12">
              <ValidatedTextInput
                name="email"
                hintText="email@example.com"
                floatingLabelText="E-mail"
                pattern={/.*@.*\..*/}
                errorMessage="Invalid email"
                fullWidth
                required
                value={this.state.email.value}
                validatedOnChange={this._change}
              />
            </Col>

            {auth.isAdmin() && !this.props.myAccount &&
              <Col lg="4" md="12">
                <SelectField
                  name="roles"
                  multiple
                  fullWidth
                  floatingLabelText="Roles"
                  hintText="Select roles"
                  value={this.state.roles.value}
                  onChange={
                    (ev, key, selected) => this._change(selected, true, 'roles')
                  }>

                  {this.possibleRoles.map((role) =>
                    <MenuItem
                      key={role}
                      insetChildren={true}
                      checked={includes(this.state.roles.value, role)}
                      value={role}
                      primaryText={role}
                    />
                  )}
                </SelectField>
              </Col>
            }
          </Row>

          <Row>
            <Col lg="4" md="12">
              <ValidatedPasswordInput
                name="password"
                value={this.state.password.value}
                floatingLabelText="Password"
                fullWidth={true}
                validatedOnChange={this._change}
              />
            </Col>

            <Col lg="4" md="12">
              <ValidatedPasswordInput
                name="passwordConfirmation"
                value={this.state.passwordConfirmation.value}
                floatingLabelText="Password confirmation"
                fullWidth={true}
                errorText={this.state.passwordMatchError}
                validatedOnChange={this._change}
              />
            </Col>
          </Row>
        </CardText>


        <CardTitle title="Contacts" />

        <CardText>
          <Row>
            <Col lg="4" md="12">
              <ValidatedTextInput
                name="address"
                hintText="123 Street Rd."
                floatingLabelText="Street address"
                fullWidth={true}
                value={this.state.address.value}
                validatedOnChange={this._change}
              />
            </Col>

            <Col lg="4" md="12">
              <ValidatedTextInput
                name="city"
                hintText="Townsville"
                floatingLabelText="City"
                fullWidth={true}
                value={this.state.city.value}
                validatedOnChange={this._change}
              />
            </Col>

            <Col lg="4" md="12">
              <ValidatedTextInput
                name="province"
                hintText="ON"
                floatingLabelText="State/Province"
                pattern={/[a-zA-Z]{2}/}
                maxLength={2}
                errorMessage="Invalid state/province code"
                fullWidth={true}
                value={this.state.province.value}
                validatedOnChange={this._change}
              />
            </Col>

            <Col lg="4" md="12">
              <ValidatedTextInput
                name="country"
                hintText="Canada"
                floatingLabelText="Country"
                fullWidth={true}
                value={this.state.country.value}
                validatedOnChange={this._change}
              />
            </Col>

            <Col lg="4" md="12">
              <ValidatedTextInput
                name="postalCode"
                hintText="A1B 2C3"
                floatingLabelText="Postal code"
                fullWidth={true}
                value={this.state.postalCode.value}
                validatedOnChange={this._change}
              />
            </Col>

            <Col lg="4" md="12">
              <ValidatedTextInput
                name="mobilePhone"
                hintText="647-555-5678"
                floatingLabelText="Mobile phone"
                fullWidth={true}
                value={this.state.mobilePhone.value}
                validatedOnChange={this._change}
              />
            </Col>

            <Col lg="4" md="12">
              <ValidatedTextInput
                name="officePhone"
                hintText="647-555-5678"
                floatingLabelText="Office phone"
                fullWidth={true}
                value={this.state.officePhone.value}
                validatedOnChange={this._change}
              />
            </Col>

            <Col lg="4" md="12">
              <ValidatedTextInput
                name="twitter"
                hintText="@person"
                floatingLabelText="Twitter handle"
                fullWidth={true}
                value={this.state.twitter.value}
                validatedOnChange={this._change}
              />
            </Col>

            <Col lg="4" md="12">
              <ValidatedTextInput
                name="facebook"
                hintText="/person"
                floatingLabelText="Facebook url path"
                fullWidth={true}
                value={this.state.facebook.value}
                validatedOnChange={this._change}
              />
            </Col>
          </Row>
        </CardText>

        <CardActions style={{textAlign: 'center'}}>
          {this.props.submitCallback &&
            <SaveButton
              type="submit"
              disabled={!this.state.formChanged || !this.state.formValid}
              onTouchTap={this._submit}
            />
          }
          {this.props.cancelCallback &&
            <CancelButton
              onTouchTap={this.props.cancelCallback}
              disabled={!this.state.formChanged && this.state.formSubmitted}
            />
          }
        </CardActions>
      </Card>
    )
  }
}

export default UserForm



// WEBPACK FOOTER //
// ./src/components/Users/UserForm.jsx